/**
 * Data Share — the askFinz button (a bookmarklet), and what the page does with what it sends.
 *
 * WHY. Some providers serve a shared chat to a person's browser but not to our server: ChatGPT and
 * Mistral answer a server with a preview or a Cloudflare check, Perplexity's data sits behind the same
 * check, and Z.ai only hands its share data to a Z.ai session. We never work around a bot check. The
 * visitor's own browser already opened the page, so the button reads the conversation THERE, the same
 * way the page itself does, and hands it to askFinz. It does this for EVERY provider (2026-11-05), so
 * one button works everywhere and nothing depends on our server being let in.
 *
 * HOW. The visitor clicks the button on an open share page. It:
 *   1. opens <askFinz>/data-share#import (synchronously, so it is not blocked as a pop-up);
 *   2. reads the conversation on the provider's own page, from the same address the page itself uses:
 *        ChatGPT           -> /share/<id>.data, the data address its page loads (the page HTML as a fallback)
 *        Mistral           -> the page's HTML fetched WITHOUT cookies first: signed in to Mistral, the owner
 *                             view of the page leaves the messages out
 *        Claude            -> /api/chat_snapshots/<id>
 *        Gemini            -> the batchexecute call its share page makes (rpc ujx1Bf), sent WITHOUT cookies:
 *                             with a signed-in Google session the call wants an anti-forgery token (400)
 *        DeepSeek          -> /api/v0/share/content?share_id=<id>
 *        Kimi              -> ChatService/GetChatShare
 *        Grok              -> /rest/app-chat/share_links/<id>
 *        Perplexity        -> /rest/thread/<slug>
 *        Z.ai              -> the share and its message bodies, with the session Z.ai gave that tab
 *                             (the token is used on chat.z.ai only and is never sent to askFinz);
 *        anything else     -> just the link, which our server reads as usual;
 *   3. waits for the askFinz page to say it is ready, then posts the result to that ONE origin.
 *
 * The page accepts a message only from its opener, only from a provider origin on the list below, and
 * only if what it parses is that provider's conversation. Everything received is treated as data and
 * goes through the same safety check and redaction as any other source.
 */

import { analyseConversation } from "./analyse";
import {
  chatgptFromData,
  claudeConversation,
  conversationFromSavedPage,
  deepseekShareConversation,
  geminiFromBatch,
  grokConversation,
  kimiConversation,
  perplexityConversation,
  zaiConversation,
} from "./normalise";
import { publicShareUrl } from "./providers";
import type { AnalysedConversation, ShareConversation, ShareProvider } from "./types";

export const READY_MESSAGE = "askfinz-datashare-ready";

/** Provider pages the button may send from, and which provider each must parse as. */
export const BUTTON_ORIGINS: Record<string, ShareProvider> = {
  "https://chatgpt.com": "chatgpt",
  "https://chat.openai.com": "chatgpt",
  "https://claude.ai": "claude",
  "https://gemini.google.com": "gemini",
  "https://chat.deepseek.com": "deepseek",
  "https://www.kimi.ai": "kimi",
  "https://kimi.ai": "kimi",
  "https://www.kimi.com": "kimi",
  "https://kimi.com": "kimi",
  "https://grok.com": "grok",
  "https://chat.mistral.ai": "mistral",
  "https://www.perplexity.ai": "perplexity",
  "https://perplexity.ai": "perplexity",
  "https://chat.z.ai": "zai",
};

/** The code that runs on the provider's page. Plain ES2017, no template literals, so it survives as a URL. */
function buttonCode(askfinzOrigin: string): string {
  const O = JSON.stringify(askfinzOrigin);
  return `(function(){
var O=${O},u=location.href,h=location.hostname.replace(/^www\\./,'');
var w=window.open(O+'/data-share#import','askfinz_datashare');
if(!w){alert('askFinz: allow pop-ups for this site, then click the button again.');return;}
var ready=false,p=null;
function flush(){if(ready&&p){w.postMessage(p,O);p=null;}}
window.addEventListener('message',function(e){if(e.source===w&&e.origin===O&&e.data==='${READY_MESSAGE}'){ready=true;flush();}});
function set(x){x.v=1;x.url=u;p=x;flush();}
function fail(e){set({kind:'error',message:String(e&&e.message||e)});}
function j(r){if(!r.ok)throw new Error('the page answered '+r.status);return r.json();}
var seg=location.pathname.split('/').filter(Boolean),id=seg[seg.length-1]||'';
try{
if(h==='chatgpt.com'||h==='chat.openai.com'){
fetch('/share/'+encodeURIComponent(id)+'.data',{credentials:'include'}).then(function(r){return r.text();}).then(function(t){if(t.indexOf('linear_conversation')<0)throw new Error('no data');set({kind:'chatgpt-data',id:id,text:t});}).catch(function(){fetch(u,{credentials:'include'}).then(function(r){return r.text();}).then(function(t){set({kind:'html',html:t});},function(){set({kind:'html',html:document.documentElement.outerHTML});});});
}else if(h==='chat.mistral.ai'){
var mOk=function(t){return t.indexOf('__next_f')>=0&&t.indexOf('contentChunks')>=0;};
fetch(u,{credentials:'omit'}).then(function(r){return r.text();}).then(function(t){if(mOk(t))return set({kind:'html',html:t});return fetch(u,{credentials:'include'}).then(function(r){return r.text();}).then(function(t2){set({kind:'html',html:mOk(t2)?t2:document.documentElement.outerHTML});});}).catch(fail);
}else if(h==='claude.ai'){
fetch('/api/chat_snapshots/'+encodeURIComponent(id)+'?rendering_mode=messages&render_all_tools=true',{credentials:'include'}).then(j).then(function(d){set({kind:'claude',id:id,data:d});}).catch(fail);
}else if(h==='gemini.google.com'){
var q=JSON.stringify([[['ujx1Bf',JSON.stringify([null,id,[2]]),null,'generic']]]);
fetch('/_/BardChatUi/data/batchexecute?rpcids=ujx1Bf&source-path='+encodeURIComponent('/share/'+id)+'&hl=en&rt=c',{method:'POST',credentials:'omit',headers:{'Content-Type':'application/x-www-form-urlencoded;charset=UTF-8'},body:'f.req='+encodeURIComponent(q)}).then(function(r){return r.text();}).then(function(t){set({kind:'gemini',id:id,text:t});}).catch(fail);
}else if(h==='chat.deepseek.com'){
fetch('/api/v0/share/content?share_id='+encodeURIComponent(id),{credentials:'include'}).then(j).then(function(d){set({kind:'deepseek',id:id,data:d});}).catch(fail);
}else if(h==='kimi.ai'||h==='kimi.com'){
fetch('/apiv2/kimi.gateway.chat.v1.ChatService/GetChatShare',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json','connect-protocol-version':'1'},body:JSON.stringify({shareId:id})}).then(j).then(function(d){set({kind:'kimi',id:id,data:d});}).catch(fail);
}else if(h==='grok.com'){
fetch('/rest/app-chat/share_links/'+encodeURIComponent(id),{credentials:'include'}).then(j).then(function(d){set({kind:'grok',id:id,data:d});}).catch(fail);
}else if(h==='perplexity.ai'){
fetch('/rest/thread/'+encodeURIComponent(id)+'?with_parent_info=true&with_schematized_response=true&version=2.18&source=default&limit=100&offset=0&from_first=true').then(j).then(function(d){set({kind:'perplexity',slug:id,data:d});}).catch(fail);
}else if(h==='chat.z.ai'){
var t=localStorage.getItem('token'),H={'Content-Type':'application/json'};if(t)H.Authorization='Bearer '+t;
fetch('/api/v1/chats/share/'+id,{headers:H}).then(j).then(function(sh){
var ids=Object.keys(((sh.chat||{}).history||{}).messages||{});
return fetch('/api/v1/chats/'+id+'/messages/batch',{method:'POST',headers:H,body:JSON.stringify({ids:ids})}).then(j).then(function(b){set({kind:'zai',id:id,share:sh,batch:b});});
}).catch(fail);
}else{set({kind:'link'});}
}catch(e){fail(e);}
})();`;
}

/** The href for the draggable button. Collapsed to one line; `%` is escaped so the URL decodes back to the code. */
export function bookmarkletHref(askfinzOrigin: string): string {
  const code = buttonCode(askfinzOrigin)
    .split("\n")
    .map((l) => l.trim())
    .join("")
    .replace(/%/g, "%25");
  return `javascript:${code}`;
}

export type ButtonResult =
  | { ok: true; kind: "parsed"; item: AnalysedConversation }
  | { ok: true; kind: "link"; url: string }
  | { ok: false; reason: string };

const ID_OK = /^[A-Za-z0-9_-]{4,120}$/;

/** Turn a message from the button into a conversation, or a link for the server to read. */
export function readButtonMessage(origin: string, data: unknown): ButtonResult {
  const provider = BUTTON_ORIGINS[origin];
  if (!provider) return { ok: false, reason: "That button message came from a site we don't accept." };
  const d = data as { v?: number; kind?: string; url?: string; [k: string]: unknown };
  if (!d || typeof d !== "object" || d.v !== 1 || typeof d.kind !== "string") {
    return { ok: false, reason: "The askFinz button sent something we couldn't read." };
  }
  const url = typeof d.url === "string" ? d.url : undefined;
  if (d.kind === "error") {
    return { ok: false, reason: `The askFinz button couldn't read that page: ${String(d.message || "unknown error").slice(0, 200)}. Make sure it is a shared chat that has finished loading.` };
  }
  if (d.kind === "link") {
    if (!url) return { ok: false, reason: "The askFinz button didn't send a link." };
    return { ok: true, kind: "link", url };
  }

  const id = typeof d.id === "string" && ID_OK.test(d.id) ? d.id : "";
  let conv: ShareConversation | null = null;
  try {
    if (d.kind === "html" && typeof d.html === "string") conv = conversationFromSavedPage(d.html);
    else if (d.kind === "chatgpt-data" && id && typeof d.text === "string") conv = chatgptFromData(d.text, "link", id, publicShareUrl("chatgpt", id));
    else if (d.kind === "perplexity" && typeof d.slug === "string") conv = perplexityConversation(d.data, d.slug, url);
    else if (d.kind === "zai" && id) conv = zaiConversation(d.share, d.batch, id, url);
    else if (d.kind === "claude" && id) conv = claudeConversation(d.data, "link", id, publicShareUrl("claude", id));
    else if (d.kind === "gemini" && id && typeof d.text === "string") conv = geminiFromBatch(d.text, id, publicShareUrl("gemini", id));
    else if (d.kind === "deepseek" && id) {
      const biz = (d.data as { data?: { biz_data?: unknown } })?.data?.biz_data;
      conv = biz ? deepseekShareConversation(biz, id, publicShareUrl("deepseek", id)) : null;
    } else if (d.kind === "kimi" && id) {
      const share = (d.data as { share?: unknown })?.share;
      conv = share ? kimiConversation(share, id, publicShareUrl("kimi", id)) : null;
    } else if (d.kind === "grok" && id) conv = grokConversation(d.data, id, publicShareUrl("grok", id));
  } catch {
    conv = null;
  }

  if (!conv || !conv.messages.length) {
    return { ok: false, reason: "We couldn't find a conversation on that page. Open a shared chat, wait for it to load, then click the button." };
  }
  // A ChatGPT page must give a ChatGPT conversation, and so on: the origin vouches for the source.
  if (conv.provider !== provider) return { ok: false, reason: "That page didn't contain a conversation from the site it came from." };
  if (!conv.url && url) conv.url = url;
  return { ok: true, kind: "parsed", item: { conversation: conv, analysis: analyseConversation(conv) } };
}
